/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  isBefore,
  isSameDay,
  isSameMonth,
  isValid,
  isWithinInterval,
  max,
  min,
  parse,
  startOfMonth,
  startOfWeek,
  toDate
} from 'date-fns';
import isString from 'lodash.isstring';
import {DateRange} from './types';

// defines the utils shared between the daterangepicker components.
export const identity = <T>(x: T) => x;

export const chunks = <T>(array: ReadonlyArray<T>, size: number): T[][] => (
  Array.from(
    {length: Math.ceil(array.length / size)},
    (_v, i) => array.slice(i * size, i * size + size)
  )
);

export const combine = (...args: any[]): string => args.filter(identity).join(' ');

// Date
export const getDaysInMonth = (date: Date) => {
  const startWeek = startOfWeek(startOfMonth(date));
  const endWeek = endOfWeek(endOfMonth(date));
  const days = [];
  for (let curr = startWeek; isBefore(curr, endWeek);) {
    days.push(curr);
    curr = addDays(curr, 1);
  }
  return days;
};

export const isStartOfRange = ({fromDate}: DateRange, day: Date) => (
  (fromDate && isSameDay(day, fromDate)) as boolean
);

export const isEndOfRange = ({toDate}: DateRange, day: Date) => (
  (toDate && isSameDay(day, toDate)) as boolean
);

export const inDateRange = ({fromDate, toDate}: DateRange, day: Date) => (
  fromDate &&
  toDate && (isWithinInterval(day, {start: fromDate, end: toDate}) ||
  isSameDay(day, fromDate) ||
  isSameDay(day, toDate))
) as boolean;

export const isRangeSameDay = ({fromDate, toDate}: DateRange) => {
  if (fromDate && toDate) {
    return isSameDay(fromDate, toDate);
  }
  return false;
};

type Falsy = false | null | undefined | 0 | '';

export const parseOptionalDate = (date: Date | string | Falsy, defaultValue: Date) => {
  if (date) {
    const val: string = !isString(date) ? date.toString() : date as string;
    const parsed = parse(val, 'yyyy-MM-dd', new Date());
    if (isValid(parsed)) {
      return parsed;
    }
  }
  return defaultValue;
};

export const getValidatedMonths = (range: DateRange, minDate: Date, maxDate: Date) => {
  const {fromDate, toDate} = range;
  if (fromDate && toDate) {
    const newStart = max([fromDate, minDate]);
    const newEnd = min([toDate, maxDate]);

    return [newStart, isSameMonth(newStart, newEnd) ? addMonths(newStart, 1) : newEnd];
  }
  return [fromDate, toDate];
};
